import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { pageText } from "../../utils/pageText";
import { buttonText } from "../../utils/buttonText";
import "./DAGSelector.css";

const PAGE_SIZE = 20;
const SOURCE_LABELS = { public: "Public DAGs", organization: "Organization DAGs", unknown: "DAG" };

export default function DAGSelector({
  items = [], value = "", subDag = "", onChange, onRefresh, loading = false,
  error, scan,
}) {
  const id = useId();
  const [source, setSource] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const resultsRef = useRef(null);
  const selected = items.find((item) => item.path === value);
  const filtered = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((item) => (source === "all" || item.source === source)
      && words.every((word) => item.path.toLowerCase().includes(word)))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [items, source, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  useEffect(() => {
    if (resultsRef.current) resultsRef.current.scrollTop = 0;
  }, [currentPage, source, query]);

  return (
    <section className="dag-selector" aria-label={pageText("DAG Selection")} aria-busy={loading}>
      <div className="dag-selector-toolbar">
        <div className="dag-selector-field">
          <label htmlFor={`${id}-source`}>{pageText("Source")}</label>
          <select id={`${id}-source`} value={source} onChange={(event) => {
            setSource(event.target.value); setPage(1);
          }}>
            <option value="all">{pageText("All sources")}</option>
            <option value="public">{pageText("Public DAGs")}</option>
            <option value="organization">{pageText("Organization DAGs")}</option>
          </select>
        </div>
        <div className="dag-selector-field dag-selector-search">
          <label htmlFor={`${id}-search`}>{pageText("Search DAGs")}</label>
          <input id={`${id}-search`} type="search" value={query}
            placeholder={pageText("Search by name or directory")}
            onChange={(event) => { setQuery(event.target.value); setPage(1); }} />
        </div>
        {onRefresh && <button type="button" className="org-btn org-btn-secondary"
          disabled={loading} onClick={onRefresh}>{buttonText("Refresh")}</button>}
      </div>
      {error && <p className="dag-selector-error" role="alert">{String(error)}</p>}
      <div className="dag-selector-summary" role="status">
        {loading ? pageText("Loading...") : pageText(`${filtered.length} matching DAGs`)}
        {scan && <span>{pageText("Public catalog: YOLO")}</span>}
      </div>
      <div ref={resultsRef} className="dag-selector-results" role="radiogroup" aria-label={pageText("Available DAGs")}>
        {visible.map((item) => {
          const parts = item.path.split("/");
          const name = parts.pop();
          return (
            <label key={item.path} className={`dag-selector-row${value === item.path ? " is-selected" : ""}`}>
              <input type="radio" name={`${id}-dag`} checked={value === item.path}
                aria-label={item.path} disabled={loading}
                onChange={() => onChange(item.path, item.sub_dags.length === 1 ? item.sub_dags[0] : "")} />
              <span className="dag-selector-name">
                <strong>{name}</strong>
                <span>{parts.join("/") || "/"}</span>
              </span>
              <span className="dag-selector-source">
                {pageText(SOURCE_LABELS[item.source] || "DAG")}
                {item.organizations?.length > 0 && <small>{item.organizations.join(", ")}</small>}
              </span>
            </label>
          );
        })}
        {!visible.length && !loading && <p className="dag-selector-empty">{pageText("No matching DAGs")}</p>}
      </div>
      <div className="dag-selector-pagination">
        <button type="button" className="org-btn org-btn-secondary" disabled={currentPage <= 1}
          onClick={() => setPage(currentPage - 1)}>{buttonText("Prev")}</button>
        <span>{pageText(`Page ${currentPage} of ${pages}`)}</span>
        <button type="button" className="org-btn org-btn-secondary" disabled={currentPage >= pages}
          onClick={() => setPage(currentPage + 1)}>{buttonText("Next")}</button>
      </div>
      {selected ? (
        <div className="dag-selector-selection">
          <span>{pageText("Selected DAG")}</span>
          <strong>{selected.path}</strong>
          <label htmlFor={`${id}-branch`}>{pageText("Sub DAG")}</label>
          <select id={`${id}-branch`} value={subDag ?? ""} disabled={loading}
            onChange={(event) => onChange(value, event.target.value === "" ? "" : Number(event.target.value))}>
            <option value="">{pageText("Choose a sub DAG")}</option>
            {selected.sub_dags.map((branch) => <option key={branch} value={branch}>DAG{branch}</option>)}
          </select>
        </div>
      ) : <p className="dag-selector-empty">{pageText(value ? "Selected DAG is no longer available" : "Select a DAG to continue")}</p>}
      {scan && <details className="dag-selector-scan">
        <summary>{pageText("Public scan details")}</summary>
        <p>{pageText(`${scan.available_count} available, ${scan.excluded_count} excluded`)}</p>
        <p>{pageText("Updated at")}: {scan.updated_at ? new Date(scan.updated_at).toLocaleString() : "—"}</p>
        {!scan.directory_exists && <p>{pageText("Public DAG directory is unavailable")}</p>}
        <p>{pageText("Catalog cached for 5 minutes. Refresh to rescan now.")}</p>
        {Object.keys(scan.rejected || {}).length > 0 && <ul>
          {Object.entries(scan.rejected).map(([path, reasons]) => <li key={path}>
            <strong>{path}</strong>
            <span>{Object.entries(reasons).map(([branch, reason]) => `${branch}: ${reason}`).join("; ")}</span>
          </li>)}
        </ul>}
      </details>}
    </section>
  );
}
